/**
 * SignInScreen: the branded page a web app shows while it hands the visitor to
 * the sign-in page, or when that page cannot be reached (Jean, 30 Oct 2026).
 *
 * The sign-in fields themselves live on the identity server's themed page
 * (infra/keycloak/themes/ops-centre for tenants, the Infinity theme for the
 * provider plane), so the apps never show an intermediate "continue" page: they
 * start the redirect at once and show this screen meanwhile. Layout follows the
 * ROBIS split and that theme: the form side on the left third, a slideshow of
 * images with text on the right two thirds.
 *
 * Product neutral and JSX-free on purpose: Ops Public, Ops Admin and 24K
 * Infinity import it through the `shared` alias, and some of their builds load
 * shared/ through the dependencies Babel rule, which does not enable JSX.
 *
 * Props
 *   product   'ops' (Ops Centre, the default) | 'infinity' (24K Infinity)
 *   status    'redirecting' (default) | 'error'
 *   context   short line above the title, e.g. the tenant or "Tenant administration"
 *   message   detail shown with the error state
 *   onRetry   called by "Try again" in the error state (reloads by default)
 */
import React from 'react';
import { OPS_CENTRE_WORDMARK, INFINITY_WORDMARK, ARKIS_WORDMARK, MARK_24K, SJ_SQUARE } from './signInMarks';
import slideOpsCentre from './slide-ops-centre.jpg';
import slideCity from './slide-city.jpg';
import slideEarth from './slide-earth.jpg';
import slideArrowHorizon from './slide-arrow-horizon.jpg';
import './signInScreen.css';

export const COMPANY = 'Surbana Technologies PTE. LTD.';

export const PRODUCTS = {
  ops: {
    name: 'Ops Centre',
    wordmark: OPS_CENTRE_WORDMARK,
    slides: [
      { image: slideOpsCentre, eyebrow: 'Ops Centre', title: 'Every operation starts here, kept up to date for your role.', text: 'Operator, IOC Station and IOC Remote, signed in with one account.' },
      { image: slideCity, eyebrow: 'Operator · live operations', title: 'Every site you look after, live in one view.', text: 'Alerts, tasks and systems of the sites your role can see, as they happen.' },
      { image: slideEarth, eyebrow: 'Digital twin', title: 'The building and everything underneath.', text: 'Assets, spaces and systems in one model, from the portfolio to the room.' },
    ],
  },
  infinity: {
    name: '24K Infinity',
    wordmark: INFINITY_WORDMARK,
    slides: [
      { image: slideArrowHorizon, position: 'center top', eyebrow: '24K Infinity', title: 'One calm view of everything you operate.', text: 'Provision tenants, coordinate deployments and keep the platform moving.' },
      { image: slideEarth, eyebrow: 'Tenants · deployments', title: 'Every tenant, its products and its health.', text: 'From the first realm to the latest release, in one place.' },
      { image: slideCity, eyebrow: 'Ops Centre · ROBIS · Nexus', title: 'The products your tenants run, from one control plane.', text: 'Releases, licences and access for each of them.' },
    ],
  },
};

/** The words of each state, kept apart from the markup so they can be tested. */
export function signInCopy(product = 'ops', status = 'redirecting') {
  const { name } = PRODUCTS[product] || PRODUCTS.ops;
  if (status === 'error') {
    return {
      eyebrow: 'Sign-in unavailable',
      title: 'We could not open the sign-in page',
      text: 'The sign-in service did not answer. Check your connection, then try again.',
      action: 'Try again',
    };
  }
  return {
    eyebrow: 'Secure sign-in',
    title: `Welcome to ${name}`,
    text: 'Opening the sign-in page…',
    action: null,
  };
}

export function copyrightLine(year = new Date().getFullYear()) {
  return `© ${year} ${COMPANY}`;
}

const h = React.createElement;

function PoweredBy() {
  return h('div', { className: 'k24-signin-powered', 'aria-label': 'Powered by 24K · ARKIS ENGINE' },
    h('span', { 'aria-hidden': true }, 'Powered by'),
    h('img', { className: 'k24-signin-24k', src: MARK_24K, alt: '' }),
    h('span', { className: 'k24-signin-dot', 'aria-hidden': true }, '·'),
    // The approved horizontal lockup: ENGINE small, to the right of the ARKIS wordmark.
    h('span', { className: 'k24-signin-lockup', 'aria-hidden': true },
      h('img', { className: 'k24-signin-arkis', src: ARKIS_WORDMARK, alt: '' }),
      h('span', { className: 'k24-signin-engine' }, 'ENGINE')));
}

export default function SignInScreen({ product = 'ops', status = 'redirecting', context, message, onRetry }) {
  const brand = PRODUCTS[product] || PRODUCTS.ops;
  const copy = signInCopy(product, status);
  const error = status === 'error';
  const retry = onRetry || (() => window.location.reload());

  return h('div', { className: `k24-signin k24-signin--${product === 'infinity' ? 'infinity' : 'ops'}` },
    h('section', { className: 'k24-signin-form' },
      h('div', { className: 'k24-signin-grid', 'aria-hidden': true }),
      h('header', { className: 'k24-signin-head' },
        h('img', { className: 'k24-signin-wordmark', src: brand.wordmark, alt: brand.name }),
        h(PoweredBy)),
      h('main', { className: 'k24-signin-body', 'aria-live': 'polite', 'aria-busy': !error },
        h('div', { className: `k24-signin-eyebrow${error ? ' is-error' : ''}` }, context && !error ? context : copy.eyebrow),
        h('h1', null, copy.title),
        h('p', { className: 'k24-signin-text' },
          !error && h('span', { className: 'k24-signin-spinner', 'aria-hidden': true }),
          copy.text),
        error && message ? h('p', { className: 'k24-signin-detail' }, message) : null,
        error ? h('button', { type: 'button', className: 'k24-signin-button', onClick: retry }, copy.action) : null),
      h('footer', { className: 'k24-signin-foot' }, copyrightLine())),
    h('section', { className: 'k24-signin-show', 'aria-hidden': true },
      brand.slides.map((slide, i) => h('div', { key: `s${i}`, className: 'k24-signin-slide', style: { '--d': `${i * 7}s` } },
        h('img', { src: slide.image, alt: '', style: slide.position ? { objectPosition: slide.position } : undefined }))),
      h('div', { className: 'k24-signin-tint' }),
      h('div', { className: 'k24-signin-shade' }),
      h('img', { className: 'k24-signin-sj', src: SJ_SQUARE, alt: '' }),
      h('div', { className: 'k24-signin-caps' },
        brand.slides.map((slide, i) => h('div', { key: `c${i}`, className: 'k24-signin-cap', style: { '--d': `${i * 7}s` } },
          h('div', { className: 'k24-signin-eyebrow' }, slide.eyebrow),
          h('h2', null, slide.title),
          h('p', null, slide.text))),
        h('div', { className: 'k24-signin-dots' },
          brand.slides.map((slide, i) => h('span', { key: `d${i}`, style: { '--d': `${i * 7}s` } }))))));
}
